import { JWMCarousel } from "@components/JWMCarousel/JWMCarousel";
import { JWMHeaderHero } from "@components/JWMHeaderHero/JWMHeaderHero";
import { JWMHero } from "@components/JWMHero/JWMHero";
import ShowPrivacyPolicyUpdate from "@components/Layout/ShowPrivacyPolicyNotification/ShowPrivacyPolicyNotification";
import { VideoPlayer } from "@components/VideoPlayer/VideoPlayer";
import { MediaImpression } from "@jmc/core/src/components/MediaImpression/MediaImpression";
import { Typography } from "@jmc/solid-design-system/src/components/atoms/Typography/Typography";
import { ImagePosition } from "@jmc/solid-design-system/src/components/molecules/Card/Card";
import { Color } from "@jmc/solid-design-system/src/components/molecules/HeaderHero/HeaderHero";
import { useJnjBranding } from "@jmc/utils/hooks/useJnjBranding";
import { CMSHeroProps } from "@jwm/types/CMSHeroProps";
import { mapJWMHeaderHeroProps, mapJWMHeroProps } from "@utils/cms-mapper/mapJWMHeroProps";
import classnames from "classnames";
import React from "react";

import { HeaderImageSize } from "../../types/CMSPage";
import style from "./style.module.scss";

interface PropTypes {
    title: string;
    headerVisual?: CMSHeroProps;
    headerSize?: HeaderImageSize;
    seoTitle?: string;
    placement?: string;
    siteSpecialty?: {
        type: string;
        title: string;
    };
    imageFocus?: string;
    customContent?: JSX.Element;
    firstSectionColor?: Color;
}

export const PageHeader = ({
    title,
    headerVisual,
    headerSize = HeaderImageSize.Large,
    placement = "none",
    siteSpecialty,
    imageFocus = "center-center",
    customContent,
    firstSectionColor = undefined,
}: PropTypes): JSX.Element => {
    const video =
        headerVisual && headerVisual?.hero_video?.length > 0
            ? headerVisual.hero_video[headerVisual?.hero_video?.length - 1].player
            : null;
    const videoData = video?.related_to?.[0];

    const carousel = headerVisual && headerVisual?.slides?.length > 0 ? headerVisual && headerVisual?.slides : null;
    const isCarouselFirstSlideTitleExsits = headerVisual?.slides?.[0]?.title?.length > 0 ? true : false;
    const { jnjFullBranded } = useJnjBranding();
    const heroProps = jnjFullBranded ? mapJWMHeaderHeroProps : mapJWMHeroProps;

    const hasCarousel = headerVisual && carousel !== null;
    const hasVideo = headerVisual && video !== null && !carousel;
    const hasHero = !video && !carousel && headerVisual;
    const hasNoTitleInVisual = title && !headerVisual?.display_title && !isCarouselFirstSlideTitleExsits;

    let heroHeight = null;
    switch (headerSize) {
        case HeaderImageSize.Large:
            heroHeight = "70vh";
            break;
        case HeaderImageSize.Medium:
            heroHeight = "45vh";
            break;
        case HeaderImageSize.Small:
            heroHeight = "30vh";
            break;
        case HeaderImageSize.XSmall:
            heroHeight = "15vh";
            break;
        default:
            heroHeight = null;
    }

    const carouselBackground =
        headerVisual?.display_options?.background_color &&
        Object.values(Color).includes(headerVisual?.display_options?.background_color?.toLowerCase() as Color)
            ? (headerVisual?.display_options?.background_color?.toLowerCase() as Color)
            : Color.DEFAULT;

    return (
        <header
            className={classnames(
                style.header,
                hasCarousel &&
                    jnjFullBranded &&
                    (carouselBackground
                        ? style[`backgroundColor__${carouselBackground}`]
                        : style[`backgroundColor__${Color.DEFAULT}`]),
            )}
            data-test-id="header"
        >
            {((hasNoTitleInVisual && !jnjFullBranded) || (hasNoTitleInVisual && hasCarousel && jnjFullBranded)) && (
                <div className={style.title} data-test-id="Header.Title">
                    <Typography variant="h1" color="white">
                        {title}
                    </Typography>
                </div>
            )}
            {hasCarousel && (
                <div className={style.hero} data-test-id="Header.Carousel">
                    <JWMCarousel
                        carouselContent={headerVisual}
                        titleComponent={"h1"}
                        placement={placement}
                        loading={"eager"}
                        headerVariant
                    />
                </div>
            )}
            {hasVideo && (
                <div className={style.hero} data-test-id="Header.Player">
                    <MediaImpression
                        type="video"
                        id={video?.related_to[0]?.video_id?.toString()}
                        player={videoData?.media_source}
                        name={video?.related_to[0]?.title}
                        placement={`${placement}|hero:${video?.related_to[0]?.title?.toString().toLowerCase()}`}
                        position={1}
                        targeturl={null}
                        autoplay={video?.autoplay}
                        mediasource={video?.related_to[0]?.media_source}
                    >
                        <VideoPlayer
                            videoId={videoData?.video_id}
                            contentAlignment={headerVisual?.display_options?.align_content?.toLowerCase()}
                            contentVerticalAlignment={headerVisual?.display_options?.align_content_vertically?.toLowerCase()}
                            hasOverlay={headerVisual?.display_options?.has_overlay}
                            autoplay={video?.autoplay}
                            showControls={video?.show_controls}
                            showLargePlayButton={video?.show_large_play_button}
                            thumbnail={videoData?.poster_image?.url}
                            showInHeader={true}
                            seoTitle={videoData?.seo_settings?.title_tag}
                            mediasource={videoData?.media_source}
                            updated_at={videoData?.updated_at}
                        />
                    </MediaImpression>
                </div>
            )}
            {hasHero && !jnjFullBranded && (
                <div className={style.hero} data-test-id="Header.Hero">
                    <JWMHero
                        {...{ ...heroProps(headerVisual), height: heroHeight }}
                        showInHeader={true}
                        fullWidth={true}
                        titleComponent={"h1"}
                        loading="eager"
                        siteSpecialty={siteSpecialty}
                        showGlossary={false}
                        imagePosition={imageFocus as ImagePosition}
                    />
                </div>
            )}
            {hasHero && jnjFullBranded && (
                <JWMHeaderHero
                    {...{ ...heroProps(headerVisual), size: headerSize }}
                    siteSpecialty={siteSpecialty}
                    showGlossary={false}
                    pageTitle={title}
                    imagePosition={headerVisual?.display_options?.image_focus || imageFocus}
                    sidesColor={firstSectionColor}
                />
            )}
            <ShowPrivacyPolicyUpdate />
            {customContent && (
                <div className={style.hero} data-test-id="Header.CustomContent">
                    {customContent}
                </div>
            )}
        </header>
    );
};

export default PageHeader;
